import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useCTAButtons } from "@/hooks/useCTAButtons";
import { ICON_MAP } from "@/components/admin/CTAButtonsAdmin";
import { openTanya } from "@/hooks/useTanyaTrigger";
import { ArrowRight } from "lucide-react";
import { useVisitorContext } from "@/contexts/VisitorTrackingContext";

interface CTAStripProps {
  buttons?: string[];
  variant?: "light" | "dark";
  className?: string;
}

export function CTAStrip({ buttons = [], variant = "light", className = "" }: CTAStripProps) {
  const { data: allCTAs } = useCTAButtons();
  const { dynamicMode, persona } = useVisitorContext();

  const glowClass = dynamicMode
    ? persona === "partner" ? "dynamic-partner-glow" : "dynamic-customer-glow"
    : "";

  const resolved = buttons
    .map((key) => {
      const cta = allCTAs?.find((c) => c.key === key);
      if (!cta) return null;
      return { key: cta.key, label: cta.label, icon: ICON_MAP[cta.icon_name] || ArrowRight };
    })
    .filter(Boolean) as { key: string; label: string; icon: React.ComponentType<{ className?: string }> }[];

  const isDark = variant === "dark";

  // Reserve space even while loading to prevent CLS.
  // Button size="lg" renders ~48px tall; match that exactly so the strip
  // does not grow when async CTA data arrives.
  if (resolved.length === 0) {
    return buttons.length > 0 ? (
      <section className={`py-6 md:py-8 ${isDark ? "bg-primary" : "bg-muted"} ${className}`}>
        <div className="container-padding mx-auto flex items-center justify-center" style={{ minHeight: '48px' }} />
      </section>
    ) : null;
  }

  return (
    <section className={`py-6 md:py-8 ${isDark ? "bg-primary" : "bg-muted"} ${className}`}>
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.5 }}
        className="container-padding mx-auto flex flex-wrap items-center justify-center gap-4"
        style={{ minHeight: '48px' }}
      >
        {resolved.map((cta, i) => {
          const Icon = cta.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.1 }}
            >
              <Button
                size="lg"
                variant={isDark ? "outline-light" : "outline"}
                className={glowClass}
                onClick={() => openTanya(cta.key)}
              >
                {cta.label}
                <Icon className="w-4 h-4" />
              </Button>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}
